import { useRouter } from 'next/router';

import { ReactNode, useEffect, useRef } from 'react';

import { useAnalytics } from '@utils/analytics';

const AD_BLOCK_INITIAL_DELAY_MS = 2500;
const AD_BLOCK_IDLE_TIMEOUT_MS = 3000;

interface AdBlockDetectorProps {
  onDetected?: (blocked: boolean) => void;
  children?: ReactNode;
}

const checkScriptLoading = (): Promise<boolean> => {
  return new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?t=${Date.now()}`;
    script.async = true;

    script.onerror = () => {
      script.remove();
      resolve(true);
    };

    script.onload = () => {
      script.remove();
      resolve(typeof window.adsbygoogle === 'undefined');
    };

    document.head.appendChild(script);
  });
};

const checkFetchRequest = async (): Promise<boolean> => {
  const testUrls = [
    'https://googleads.g.doubleclick.net/pagead/id',
    'https://pagead2.googlesyndication.com/pagead/show_ads.js',
  ];

  try {
    const results = await Promise.all(
      testUrls.map(async (url) => {
        try {
          await fetch(url, {
            method: 'HEAD',
            mode: 'no-cors',
            cache: 'no-cache',
          });
          return 'loaded';
        } catch {
          return 'blocked';
        }
      })
    );

    return results.every((result) => result === 'blocked');
  } catch {
    return true;
  }
};

export function AdBlockDetector({
  onDetected,
  children,
}: AdBlockDetectorProps) {
  const router = useRouter();
  const { firebase } = useAnalytics();
  const pathnameRef = useRef(router.pathname);
  const firebaseRef = useRef(firebase);
  const onDetectedRef = useRef(onDetected);

  pathnameRef.current = router.pathname;
  firebaseRef.current = firebase;
  onDetectedRef.current = onDetected;

  useEffect(() => {
    let delayId: number | undefined;
    let idleId: number | undefined;

    const detectAdBlock = async () => {
      try {
        let isAdBlocked = false;
        const maxRetries = 3;

        for (let attempt = 0; attempt < maxRetries; attempt++) {
          if (attempt > 0) {
            await new Promise((resolve) => setTimeout(resolve, 1000));
          }

          const results = await Promise.all([
            checkScriptLoading(),
            checkFetchRequest(),
          ]);

          console.log(`AdBlock Detection Results (attempt ${attempt + 1}):`, {
            page: pathnameRef.current,
            scriptLoading: results[0],
            fetchRequest: results[1],
          });

          isAdBlocked = results[0] || results[1];

          if (!isAdBlocked) {
            break;
          }
        }

        onDetectedRef.current?.(isAdBlocked);

        if (isAdBlocked) {
          firebaseRef.current.logEvent('ad_block', {});
        }
      } catch (error) {
        console.error('AdBlock detection error:', error);
      }
    };

    const runDetection = () => {
      void detectAdBlock();
    };
    const scheduleDetection = () => {
      delayId = window.setTimeout(() => {
        if (typeof window.requestIdleCallback === 'function') {
          idleId = window.requestIdleCallback(runDetection, {
            timeout: AD_BLOCK_IDLE_TIMEOUT_MS,
          });
          return;
        }

        runDetection();
      }, AD_BLOCK_INITIAL_DELAY_MS);
    };

    if (document.readyState === 'complete') {
      scheduleDetection();
    } else {
      window.addEventListener('load', scheduleDetection, { once: true });
    }

    return () => {
      window.removeEventListener('load', scheduleDetection);

      if (delayId !== undefined) {
        window.clearTimeout(delayId);
      }
      if (idleId !== undefined) {
        window.cancelIdleCallback(idleId);
      }
    };
  }, []);

  return <>{children}</>;
}

export default AdBlockDetector;
